export interface Settings {
    master: number;
    music: number;
    effects: number;
    ambience: number;
    dialogue: number;
    reducedMotion: boolean;
    contrast: boolean;
    textScale: number;
    autoDialogue: boolean;
    keys: Record<string, string>;
    deadZone: number;
    vibration: boolean;
    pad: Record<string, number>;
}
export interface RecordEntry {
    seed: number;
    route: number;
    hard: boolean;
    floor: number;
    victory: boolean;
    seconds: number;
    memory: number;
    ending: string | null;
    date: string;
}
export interface Profile {
    version: 2;
    routes: number;
    achievements: string[];
    collectibles: string[];
    seen: string[];
    endings: string[];
    memory: number;
    records: RecordEntry[];
    settings: Settings;
}
export const defaults: Settings = { master: .6, music: .45, effects: .65, ambience: .3, dialogue: .45, reducedMotion: false, contrast: false, textScale: 1, autoDialogue: true, keys: { pause: 'Space', dialogue: 'KeyD', wake: 'KeyW', reroll: 'KeyR', previous: 'ArrowLeft', next: 'ArrowRight', important: 'KeyI', normal: 'KeyN', ignore: 'KeyX', camp: 'KeyC', confirm: 'Enter', cancel: 'Escape', draft1: 'Digit1', draft2: 'Digit2', draft3: 'Digit3', draft4: 'Digit4', draft5: 'Digit5' }, deadZone: .35, vibration: true, pad: { confirm: 0, cancel: 1, camp: 2, wake: 3, previous: 4, next: 5, pause: 9 } };
const strings = (value: unknown) => Array.isArray(value) ? Array.from(new Set(value.filter((v): v is string => typeof v === 'string'))) : [];
const bounded = (v: unknown, fallback: number, min = 0, max = 1) => typeof v === 'number' && Number.isFinite(v) ? Math.max(min, Math.min(max, v)) : fallback;
export function loadProfile(): Profile {
    const fresh: Profile = { version: 2, routes: 1, achievements: [], collectibles: [], seen: [], endings: [], memory: 0, records: [], settings: { ...defaults, keys: { ...defaults.keys }, pad: { ...defaults.pad } } };
    let raw: string | null = null;
    try {
        raw = localStorage.getItem('aporia-profile-v2');
        if (!raw)
            return fresh;
        const p = JSON.parse(raw);
        if (p?.version !== 2 || typeof p !== 'object')
            throw new Error('Unsupported profile');
        const settings = { ...fresh.settings };
        const incoming = p.settings || {};
        for (const key of ['master', 'music', 'effects', 'ambience', 'dialogue'] as const)
            settings[key] = bounded(incoming[key], defaults[key]);
        for (const key of ['reducedMotion', 'contrast', 'autoDialogue', 'vibration'] as const)
            settings[key] = typeof incoming[key] === 'boolean' ? incoming[key] : defaults[key];
        settings.textScale = bounded(incoming.textScale, 1, .9, 1.2);
        settings.deadZone = bounded(incoming.deadZone, .35, .1, .8);
        for (const key of Object.keys(settings.keys))
            if (typeof incoming.keys?.[key] === 'string' && incoming.keys[key].length < 32)
                settings.keys[key] = incoming.keys[key];
        for (const key of Object.keys(settings.pad))
            settings.pad[key] = Math.floor(bounded(incoming.pad?.[key], defaults.pad[key], 0, 11));
        const achievements = strings(p.achievements);
        const routes = Math.max(Math.floor(bounded(p.routes, 1, 1, 3)), achievements.includes('ACH_LOOP_II_CLEAR') ? 3 : achievements.includes('ACH_LOOP_I_CLEAR') ? 2 : 1);
        const records: RecordEntry[] = Array.isArray(p.records) ? p.records.filter((r: RecordEntry) => r && Number.isFinite(r.seed) && Number.isFinite(r.seconds) && r.route >= 1 && r.route <= 3 && r.floor >= 1 && r.floor <= 13).slice(0, 50) : [];
        return { ...fresh, routes, achievements, collectibles: strings(p.collectibles), seen: strings(p.seen), endings: strings(p.endings), memory: bounded(p.memory, 0, 0, Number.MAX_SAFE_INTEGER), records, settings };
    }
    catch {
        if (raw) {
            try {
                localStorage.setItem('aporia-profile-recovery', raw);
            }
            catch { /* Retain a playable in-memory profile if storage is unavailable. */ }
        }
        return fresh;
    }
}
export function saveProfile(p: Profile) { try {
    localStorage.setItem('aporia-profile-v2', JSON.stringify(p));
    return true;
}
catch {
    return false;
} }
